import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  History, Search, Filter, Calendar, User, Car, Shield, Wrench,
  ClipboardCheck, AlertCircle, Eye, Download, RefreshCw, Clock,
  ChevronLeft, ChevronRight, FileText
} from 'lucide-react';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';
import { useFormat } from '../../i18n/format.js';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// Entity type configuration — labelKey resolved at render; reuses EmailPreviewModal's
// entityTypes keys where the French text is identical (all but insurance).
const ENTITY_TYPES = {
  driver: { labelKey: 'emails.previewModal.entityTypes.driver', icon: User, color: 'bg-green-100 text-green-800' },
  vehicle: { labelKey: 'emails.previewModal.entityTypes.vehicle', icon: Car, color: 'bg-purple-100 text-purple-800' },
  claim: { labelKey: 'emails.previewModal.entityTypes.claim', icon: AlertCircle, color: 'bg-red-100 text-red-800' },
  maintenance_request: { labelKey: 'emails.previewModal.entityTypes.maintenance', icon: Wrench, color: 'bg-orange-100 text-orange-800' },
  inspection: { labelKey: 'emails.previewModal.entityTypes.inspection', icon: ClipboardCheck, color: 'bg-yellow-100 text-yellow-800' },
  insurance_contract: { labelKey: 'emails.history.entityTypes.insurance', icon: Shield, color: 'bg-blue-100 text-blue-800' }
};

function GeneratedEmailsHistory({ onMessage }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [searchTerm, setSearchTerm] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all'); // 'all' | 'today' | 'week' | 'month'
  const [page, setPage] = useState(1);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const pageSize = 20;

  // Fetch generated emails
  const { data: emailsData, isLoading, refetch } = useQuery({
    queryKey: ['generatedEmails'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/generated_emails`, {
        headers: getAuthHeaders()
      });
      return res.json();
    }
  });

  const allEmails = emailsData?.data || [];

  // Filter emails
  const filteredEmails = allEmails.filter(email => {
    // Search filter
    const matchesSearch = searchTerm === '' ||
      email.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.template_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.recipient_email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Entity type filter
    const matchesEntityType = entityTypeFilter === 'all' || email.entity_type === entityTypeFilter;

    // Date filter
    let matchesDate = true;
    if (dateFilter !== 'all' && email.generated_at) {
      const emailDate = new Date(email.generated_at);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      if (dateFilter === 'today') {
        matchesDate = emailDate >= today;
      } else if (dateFilter === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        matchesDate = emailDate >= weekAgo;
      } else if (dateFilter === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        matchesDate = emailDate >= monthAgo;
      }
    }

    return matchesSearch && matchesEntityType && matchesDate;
  });

  // Pagination
  const totalPages = Math.ceil(filteredEmails.length / pageSize);
  const paginatedEmails = filteredEmails.slice((page - 1) * pageSize, page * pageSize);

  const formatDate = (dateStr) => {
    if (!dateStr) return t('common.states.notAvailable');
    return fmt.dateTime(dateStr);
  };

  const handleViewEmail = (email) => {
    setSelectedEmail(email);
  };

  const handleDownloadEml = async (email) => {
    try {
      // Create EML content from the stored email
      const emlContent = createEmlContent(email);
      const blob = new Blob([emlContent], { type: 'message/rfc822' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email_${email.id}_${email.template_code}.eml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      onMessage?.({
        title: t('common.messages.successTitle'),
        message: t('emails.previewModal.downloadSuccess'),
        type: 'success'
      });
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message,
        type: 'error'
      });
    }
  };

  const createEmlContent = (email) => {
    const date = new Date(email.generated_at || Date.now()).toUTCString();
    const boundary = `----=_Part_${Date.now()}`;

    let eml = `From: FleetManager <noreply@fleetmanager.local>\r\n`;
    eml += `To: ${email.recipient_email || 'recipient@example.com'}\r\n`;
    eml += `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(email.subject || 'No Subject')))}?=\r\n`;
    eml += `Date: ${date}\r\n`;
    eml += `MIME-Version: 1.0\r\n`;
    eml += `Content-Type: multipart/alternative; boundary="${boundary}"\r\n`;
    eml += `X-Generated-By: FleetManager Email Generator\r\n`;
    eml += `X-Template-Code: ${email.template_code || 'N/A'}\r\n`;
    eml += `\r\n`;

    // Text part
    eml += `--${boundary}\r\n`;
    eml += `Content-Type: text/plain; charset="UTF-8"\r\n`;
    eml += `Content-Transfer-Encoding: quoted-printable\r\n`;
    eml += `\r\n`;
    eml += `${email.body_text || ''}\r\n`;
    eml += `\r\n`;

    // HTML part (if available)
    if (email.body_html) {
      eml += `--${boundary}\r\n`;
      eml += `Content-Type: text/html; charset="UTF-8"\r\n`;
      eml += `Content-Transfer-Encoding: quoted-printable\r\n`;
      eml += `\r\n`;
      eml += `${email.body_html}\r\n`;
      eml += `\r\n`;
    }

    eml += `--${boundary}--\r\n`;

    return eml;
  };

  const EntityIcon = ({ type }) => {
    const config = ENTITY_TYPES[type];
    const Icon = config?.icon || FileText;
    return <Icon className="w-4 h-4" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('emails.history.search.placeholder')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={entityTypeFilter}
            onChange={(e) => { setEntityTypeFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">{t('emails.history.filters.allEntities')}</option>
            {Object.entries(ENTITY_TYPES).map(([key, config]) => (
              <option key={key} value={key}>{t(config.labelKey)}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-400" />
          <select
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
          >
            <option value="all">{t('emails.history.filters.allDates')}</option>
            <option value="today">{t('emails.history.today')}</option>
            <option value="week">{t('emails.history.filters.thisWeek')}</option>
            <option value="month">{t('emails.history.filters.thisMonth')}</option>
          </select>
        </div>

        <button
          onClick={() => refetch()}
          className="p-2 text-gray-600 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
          title={t('common.actions.refresh')}
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-gray-900">{allEmails.length}</div>
          <div className="text-sm text-gray-500">{t('emails.history.stats.totalGenerated')}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {allEmails.filter(e => {
              const d = new Date(e.generated_at);
              const today = new Date();
              return d.toDateString() === today.toDateString();
            }).length}
          </div>
          <div className="text-sm text-gray-500">{t('emails.history.today')}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-600">
            {new Set(allEmails.map(e => e.template_code)).size}
          </div>
          <div className="text-sm text-gray-500">{t('emails.history.stats.templatesUsed')}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-purple-600">
            {new Set(allEmails.map(e => e.entity_type)).size}
          </div>
          <div className="text-sm text-gray-500">{t('emails.history.stats.entityTypes')}</div>
        </div>
      </div>

      {/* Table */}
      {filteredEmails.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">{t('emails.history.empty.title')}</h3>
          <p className="text-gray-500 mt-1">
            {searchTerm || entityTypeFilter !== 'all' || dateFilter !== 'all'
              ? t('emails.templatesView.empty.filtered')
              : t('emails.history.empty.default')}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.date')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.template')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.subject')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.entity')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.recipient')}</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t('emails.history.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedEmails.map(email => {
                  const entityConfig = ENTITY_TYPES[email.entity_type] || {};
                  return (
                    <tr key={email.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(email.generated_at)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded">
                          {email.template_code}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                        {email.subject}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${entityConfig.color || 'bg-gray-100 text-gray-800'}`}>
                          <EntityIcon type={email.entity_type} />
                          #{email.entity_id}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {email.recipient_email || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-1">
                          <button
                            onClick={() => handleViewEmail(email)}
                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                            title={t('common.actions.view')}
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDownloadEml(email)}
                            className="p-1.5 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded"
                            title={t('emails.previewModal.downloadEml')}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {t('emails.history.pagination.range', {
                  start: ((page - 1) * pageSize) + 1,
                  end: Math.min(page * pageSize, filteredEmails.length),
                  total: filteredEmails.length
                })}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-3 py-1 text-sm">
                  {t('emails.history.pagination.pageLabel', { page, total: totalPages })}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Email Detail Modal */}
      {selectedEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold">{t('emails.history.detail.title')}</h2>
                  <p className="text-gray-300 text-sm">{selectedEmail.template_code}</p>
                </div>
                <button
                  onClick={() => setSelectedEmail(null)}
                  className="text-white hover:text-gray-300"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('emails.history.detail.generatedDate')}</div>
                    <div className="font-medium">{formatDate(selectedEmail.generated_at)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">{t('emails.history.table.recipient')}</div>
                    <div className="font-medium">{selectedEmail.recipient_email || '-'}</div>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('emails.history.table.subject')}</div>
                  <div className="font-medium text-lg">{selectedEmail.subject}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">{t('emails.history.detail.body')}</div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <pre className="whitespace-pre-wrap font-sans text-gray-700">
                      {selectedEmail.body_text}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setSelectedEmail(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
              >
                {t('common.actions.close')}
              </button>
              <button
                onClick={() => handleDownloadEml(selectedEmail)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {t('emails.previewModal.downloadEml')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GeneratedEmailsHistory;
